import React, { useMemo } from 'react';
import { receive, aggregateEvents, patchEvent } from '@onward-delivery/core';

import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { useFormatters } from './hooks';
import { Header1, Body1, Tag, Step, SubStepDesktop, SubStepMobile, Anchor } from './blocks';
import { Alert } from '@material-ui/lab';
import { OnwardLink } from '@/styles/blocks';
import { formatInTimeZone } from 'date-fns-tz';
import { formatCalendarDate } from './utils';

export default function Received({ isDesktop, isLoading, warehouse, before, after, order, tz, callbacks, readOnly }) {
    const isAfterAttempt = before?.transition?.next === 'WAREHOUSE_RETURN';
    let receivedEvent = {};
    switch (true) {
        case before?.transition?.next === 'WAREHOUSE_RETURN':
            receivedEvent = before;
            break;
        case after?.action === 'AWAITING:RECEIVED':
        case after?.action === 'AWAITING:ADD_MM_CD':
            receivedEvent = order.wh_events.findLast((e) => e.transition?.next === 'RECEIVING');
    }

    const STEPS = [
        {
            description: `Mark warehouse status as 'Received'`,
            complete: ({ order, before, after }) => {
                return receivedEvent?.status === 'RECEIVED';
            },
            ...(!readOnly
                ? {
                      cta: 'Mark Received',
                      secondaryCta: 'Add Exception',
                      callback: ({ order, before, after }) => {
                          let events = [],
                              action = null;
                          if (isAfterAttempt) {
                              events = [before];
                              action = before.action;
                          } else {
                              // pass the existing event through; patchEvent can only patch a row already in curr
                              events = !!receivedEvent?.event_id ? [receivedEvent] : receive(order, {});
                              action = !!receivedEvent?.event_id
                                  ? receivedEvent.action
                                  : events[events.length - 1].action;
                          }

                          callbacks.editReceiveEvent({
                              events,
                              action: action,
                              timestampLabel: 'Received Date',
                              description1: `Are you sure you want to mark the warehouse status as 'Received'?`,
                              description2: `This will let the shipper know the order has been received into your warehouse`,
                              timestampAttr: 'received_date',
                              params: {},
                          });
                      },
                      secondaryCallback: ({ order, before, after }) => {
                          callbacks.createException({
                              type: 'PICKUP',
                          });
                      },
                  }
                : {}),
        },
    ];

    const isComplete = useMemo(() => {
        return STEPS.map((step) => {
            return step.complete({ order, before, after, warehouse });
        });
    }, [before, after, order]);

    const inProgressIdx = useMemo(() => {
        return isComplete.findIndex((x) => !x);
    }, [isComplete]);

    return (
        <>
            <Step
                isDesktop={isDesktop}
                isComplete={isComplete.every((x) => x)}
                inProgress={!isComplete.every((x) => x)}
                label={() => {
                    return (
                        <Grid
                            direction="row"
                            container
                            css={css`
                                align-items: center;
                            `}
                        >
                            <Grid
                                direction="column"
                                css={css`
                                    margin-right: 8px;
                                `}
                            >
                                <Header1>Received</Header1>
                            </Grid>
                            {receivedEvent?.received_date ? (
                                <>
                                    <Grid
                                        direction="column"
                                        css={css`
                                            margin-right: 8px;
                                        `}
                                    >
                                        <Tag>
                                            <Body1
                                                css={css`
                                                    margin-right: 20px;
                                                `}
                                            >{`Received: ${
                                                receivedEvent?.received_date
                                                    ? formatInTimeZone(
                                                          receivedEvent.received_date,
                                                          tz,
                                                          'EEE, MMM dd, yyyy, h:mm a zzz'
                                                      )
                                                    : 'Pending'
                                            }`}</Body1>
                                            {!readOnly && (
                                                <Anchor
                                                    disabled={!receivedEvent.received_date}
                                                    onClick={() => STEPS[0].callback({ order, before, after })}
                                                >
                                                    <Header1>Edit</Header1>
                                                </Anchor>
                                            )}
                                        </Tag>
                                    </Grid>
                                </>
                            ) : (
                                <Grid
                                    direction="column"
                                    css={css`
                                        margin-right: 8px;
                                    `}
                                >
                                    <Tag>
                                        <Body1
                                            css={css`
                                                margin-right: 20px;
                                            `}
                                        >{`Est Ship: ${
                                            before?.est_ship_date
                                                ? formatCalendarDate(before.est_ship_date, tz)
                                                : 'Pending'
                                        }`}</Body1>
                                        <Body1>{`Est Received: ${
                                            before?.est_received_date
                                                ? formatCalendarDate(before.est_received_date, tz)
                                                : 'Pending'
                                        }`}</Body1>
                                    </Tag>
                                </Grid>
                            )}
                        </Grid>
                    );
                }}
            >
                {() => (
                    <>
                        {STEPS.map(({ description, cta, secondaryCta, callback, secondaryCallback }, idx) => {
                            const SubStep = isDesktop ? SubStepDesktop : SubStepMobile;

                            return (
                                <SubStep
                                    key={idx}
                                    isComplete={isComplete[idx]}
                                    inProgress={inProgressIdx === idx}
                                    isLoading={isLoading}
                                    isLast={idx === STEPS.length - 1}
                                    description={description}
                                    cta={cta}
                                    secondaryCta={secondaryCta}
                                    callback={
                                        callback
                                            ? () => {
                                                  callback({ before, after, order });
                                              }
                                            : null
                                    }
                                    secondaryCallback={secondaryCallback}
                                    secondaryOrange={true}
                                />
                            );
                        })}
                    </>
                )}
            </Step>
        </>
    );
}
